import Seo from "../components/Seo";
import PageHero from "../components/site/PageHero";
import { AlertTriangle } from "lucide-react";

const DraftBanner = () => (
  <div className="flex items-start gap-3 bg-sandstone/60 border border-gold/50 p-5 mb-10" data-testid="policy-draft-banner">
    <AlertTriangle size={20} strokeWidth={1.5} className="text-gold-dark shrink-0 mt-0.5" aria-hidden="true" />
    <p className="text-sm text-charcoal/80 leading-relaxed">
      <strong>Draft — pending trustee review and adoption.</strong> This document is provided in
      draft form for transparency and is subject to formal approval by the trustees of Mount
      Moriah Relief before adoption. Please treat it as a working document.
    </p>
  </div>
);

function PolicyLayout({ eyebrow, title, intro, children, path, description }) {
  return (
    <div>
      <Seo title={title} path={path} description={description} />
      <PageHero eyebrow={eyebrow} title={title}>
        <p>{intro}</p>
      </PageHero>
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <DraftBanner />
          <div className="space-y-10" data-testid="policy-content">{children}</div>
        </div>
      </section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-serif text-2xl font-semibold text-forest">{title}</h2>
    <div className="mt-4 space-y-4 text-sm text-charcoal/80 leading-relaxed">{children}</div>
  </section>
);

export function PrivacyPolicy() {
  return (
    <PolicyLayout
      eyebrow="Policies" title="Privacy Policy" path="/privacy-policy"
      description="How Mount Moriah Relief collects, uses and protects personal information, in line with UK data-protection law."
      intro="How we collect, use and protect your personal information."
    >
      <Section title="Who we are">
        <p>Mount Moriah Relief is a charitable organisation established in Northern Ireland in 2024. For data-protection purposes we are the controller of personal data collected through this website. You can contact us at <a className="text-forest underline" href="mailto:admin@mountmoriahrelief.org">admin@mountmoriahrelief.org</a>.</p>
      </Section>
      <Section title="What we collect">
        <p>Contact and volunteer forms: your name, email address and message, used solely to respond to your enquiry.</p>
        <p>Newsletter: your email address, used only to send updates you have explicitly confirmed you want (double opt-in). Consent records are kept so we can demonstrate your choice.</p>
        <p>We do not collect payment card details. Donations are processed by JustGiving under its own privacy policy.</p>
      </Section>
      <Section title="How we use and protect information">
        <p>Personal data is stored securely, accessible only to authorised administrators, and never sold or publicly displayed. We keep personal information only for as long as it is needed for the purpose it was collected, after which it is deleted under our retention schedule.</p>
        <p>We do not load non-essential analytics or tracking technologies without consent.</p>
      </Section>
      <Section title="Your rights">
        <p>Under the UK GDPR and the Data Protection Act 2018 you have the right to access, correct, erase or restrict the use of your personal data, and to object to processing. To exercise any of these rights, email <a className="text-forest underline" href="mailto:admin@mountmoriahrelief.org">admin@mountmoriahrelief.org</a>. You can also complain to the Information Commissioner's Office (ICO).</p>
        <p>Every newsletter email includes an unsubscribe link, and you can unsubscribe at any time without an account.</p>
      </Section>
    </PolicyLayout>
  );
}

export function CookiePolicy() {
  return (
    <PolicyLayout
      eyebrow="Policies" title="Cookie Policy" path="/cookie-policy"
      description="How Mount Moriah Relief uses cookies — essential cookies only, no tracking without consent."
      intro="How this website uses cookies."
    >
      <Section title="What we use">
        <p>This website uses only essential cookies. The administrative dashboard uses a single secure, HttpOnly session cookie so authorised administrators can stay signed in. This cookie is not used for visitors browsing the public site.</p>
      </Section>
      <Section title="What we do not use">
        <p>We do not use advertising cookies, cross-site trackers or non-essential analytics cookies. If we ever introduce optional analytics, they will be off by default and enabled only with your consent through a cookie preference control.</p>
      </Section>
      <Section title="Managing cookies">
        <p>You can control or delete cookies through your browser settings at any time. Blocking the essential administrative session cookie will only affect administrators signing in — the public website works without it.</p>
      </Section>
    </PolicyLayout>
  );
}

export function Safeguarding() {
  return (
    <PolicyLayout
      eyebrow="Policies" title="Safeguarding" path="/safeguarding"
      description="Mount Moriah Relief's safeguarding principles for protecting the people we serve and work with."
      intro="Our commitment to protecting every person we serve and work with."
    >
      <Section title="Our commitment">
        <p>Mount Moriah Relief is committed to protecting children and adults at risk from harm, abuse and exploitation in everything we do. Safeguarding is the responsibility of everyone involved with the charity, and our trustees hold ultimate oversight.</p>
      </Section>
      <Section title="Working with partners">
        <p>We expect the organisations we collaborate with to share our safeguarding standards. Beneficiary stories and images are published only with informed consent and are always handled with dignity.</p>
      </Section>
      <Section title="Raising a concern">
        <p>If you have a safeguarding concern relating to our work, please contact us immediately at <a className="text-forest underline" href="mailto:admin@mountmoriahrelief.org">admin@mountmoriahrelief.org</a>. Concerns are treated seriously and handled confidentially.</p>
      </Section>
    </PolicyLayout>
  );
}

export function Complaints() {
  return (
    <PolicyLayout
      eyebrow="Policies" title="Complaints Procedure" path="/complaints"
      description="How to raise a complaint with Mount Moriah Relief and how we handle it."
      intro="How to tell us when something is wrong — and what we do about it."
    >
      <Section title="How to complain">
        <p>Email <a className="text-forest underline" href="mailto:admin@mountmoriahrelief.org">admin@mountmoriahrelief.org</a> with the subject "Complaint", or use our <a className="text-forest underline" href="/contact">contact form</a>. Please include your name, contact details and a description of your concern.</p>
      </Section>
      <Section title="What happens next">
        <p>We acknowledge complaints and investigate them fairly and promptly. Where possible, complaints are reviewed by a trustee who was not involved in the matter. We will tell you the outcome and any action we are taking.</p>
      </Section>
      <Section title="If you are not satisfied">
        <p>If you remain dissatisfied, you may escalate your concern to the appropriate charity regulator once our charitable registration is confirmed, or to the ICO for data-protection matters.</p>
      </Section>
    </PolicyLayout>
  );
}

export function Accessibility() {
  return (
    <PolicyLayout
      eyebrow="Policies" title="Accessibility Statement" path="/accessibility"
      description="Mount Moriah Relief's commitment to an accessible website, aiming for WCAG 2.2 AA."
      intro="Our commitment to a website everyone can use."
    >
      <Section title="Our commitment">
        <p>Mount Moriah Relief aims for this website to conform to WCAG 2.2 Level AA. We use semantic page structure, descriptive alternative text, keyboard-accessible navigation, visible focus indicators, sufficient colour contrast, and animations that respect reduced-motion preferences.</p>
      </Section>
      <Section title="Known limitations">
        <p>As a newly built website, some areas may not yet meet every requirement. We review accessibility as content is added and welcome reports of any barrier you encounter.</p>
      </Section>
      <Section title="Feedback">
        <p>If you have difficulty using any part of this website, contact us at <a className="text-forest underline" href="mailto:admin@mountmoriahrelief.org">admin@mountmoriahrelief.org</a> and we will do our best to help and to fix the issue.</p>
      </Section>
    </PolicyLayout>
  );
}
